import { useEffect, useState } from "react";
import { AlertTriangle, Megaphone, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

/**
 * Compact whiteboard banner shown at the top of every dashboard.
 * - Emergency posts render as a red band; everything else stacks below in navy.
 * - Each visible post has a dismiss X (marks the post read for this user;
 *   the post itself stays live for everyone else until its expiry).
 * - Falls back to nothing (no wrapper) when there are no active posts.
 */
export default function WhiteboardBanner({ maxNormal = 2 }) {
    const [posts, setPosts] = useState([]);

    async function load() {
        try {
            const { data } = await api.get("/whiteboard/active");
            setPosts(data);
        } catch (e) {
            // Silent fail on banner — never block a dashboard from rendering
            // eslint-disable-next-line no-console
            console.warn("whiteboard banner load failed", apiError(e));
        }
    }
    useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

    async function dismiss(pid) {
        try {
            await api.post(`/whiteboard/${pid}/read`);
            setPosts((ps) => ps.filter((p) => p.id !== pid));
        } catch (e) { toast.error(apiError(e)); }
    }

    if (!posts.length) return null;
    const emergency = posts.filter((p) => p.kind === "EMERGENCY");
    const normal = posts.filter((p) => p.kind !== "EMERGENCY").slice(0, maxNormal);
    return (
        <div className="space-y-2 mb-4" data-testid="whiteboard-banner">
            {emergency.map((p) => (
                <div key={p.id} className="rounded-lg bg-[#E02B2B] text-white px-4 py-3 flex items-start gap-3 shadow-sm" data-testid={`whiteboard-emergency-${p.id}`}>
                    <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-widest">Emergency</span>
                            <span className="text-[10px] opacity-80">·  {p.posted_by_name}  ·  {p.posted_at?.slice(0, 16).replace("T", " ")}</span>
                        </div>
                        <div className="font-semibold text-sm mt-0.5">{p.title}</div>
                        <div className="text-xs whitespace-pre-wrap mt-1 opacity-95">{p.body}</div>
                    </div>
                    <button aria-label="Dismiss" onClick={() => dismiss(p.id)} className="text-white/80 hover:text-white p-1"><X className="w-4 h-4" /></button>
                </div>
            ))}
            {normal.map((p) => (
                <div key={p.id} className="rounded-lg bg-[#1E3355]/95 text-white px-4 py-3 flex items-start gap-3 shadow-sm" data-testid={`whiteboard-post-${p.id}`}>
                    <Megaphone className="w-4 h-4 shrink-0 mt-0.5 text-white/80" />
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-white/70">Announcement · {p.posted_by_name} · {p.posted_at?.slice(0, 16).replace("T", " ")}</div>
                        <div className="font-semibold text-sm mt-0.5 truncate">{p.title}</div>
                        <div className="text-xs whitespace-pre-wrap mt-1 text-white/90 line-clamp-2">{p.body}</div>
                        <Link to="/announcements" className="text-[11px] text-white underline mt-1 inline-block" data-testid={`whiteboard-post-${p.id}-open`}>Open board →</Link>
                    </div>
                    <button aria-label="Dismiss" onClick={() => dismiss(p.id)} className="text-white/70 hover:text-white p-1"><X className="w-4 h-4" /></button>
                </div>
            ))}
        </div>
    );
}
